// @ts-nocheck
/* eslint-disable */
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import {
  listHealthScreenings,
  saveHealthScreening,
  deleteHealthScreening,
  type HealthScreeningRow,
} from "@/lib/screenings.functions";
import { statusFor, type ScreeningStatus } from "./screeningStatus";

// Palette — match LabUploads / Care tab.
const TD = "#4B1F4F";
const RO = "#FF5C7A";
const GR = "#6E4B71";
const GL = "#FAF7F5";
const GD = "#6E4B71";
const WH = "#ffffff";

type ScreeningKey =
  | "mammogram"
  | "dexa"
  | "lipid_panel"
  | "a1c"
  | "colonoscopy"
  | "pap_hpv"
  | "blood_pressure"
  | "eye_exam";

const SCREENINGS: { key: ScreeningKey; emoji: string; name: string; interval: string; postmenoOnly?: boolean }[] = [
  { key: "mammogram",      emoji: "🎀", name: "Mammogram",            interval: "(1 yr)",  postmenoOnly: true },
  { key: "dexa",           emoji: "🦴", name: "Bone density (DEXA)",  interval: "(2 yr)",  postmenoOnly: true },
  { key: "lipid_panel",    emoji: "🫀", name: "Lipid panel",          interval: "(2 yr)"  },
  { key: "a1c",            emoji: "🩸", name: "A1C / blood sugar",    interval: "(1 yr)"  },
  { key: "colonoscopy",    emoji: "🔬", name: "Colonoscopy",          interval: "(10 yr)", postmenoOnly: true },
  { key: "pap_hpv",        emoji: "🌸", name: "Pap / HPV",            interval: "(5 yr)"  },
  { key: "blood_pressure", emoji: "💗", name: "Blood pressure",       interval: "(6 mo)"  },
  { key: "eye_exam",       emoji: "👁️", name: "Eye exam",             interval: "(2 yr)"  },
];

type Status = ScreeningStatus;

function statusPill(status: Status) {
  const map: Record<Status, { text: string; bg: string; color: string }> = {
    up_to_date:   { text: "Up to date", bg: "#DCEFE2", color: "#1F6F3D" },
    due_soon:     { text: "Due soon",   bg: "#FFE3D1", color: "#A04A12" },
    overdue:      { text: "Overdue",    bg: "#FFD0D9", color: RO        },
    never_logged: { text: "Not logged", bg: "#EFEAEC", color: GR        },
  };
  const s = map[status];
  return (
    <span style={{ background: s.bg, color: s.color, fontSize: 11, fontWeight: 500, padding: "4px 10px", borderRadius: 999, whiteSpace: "nowrap" }}>
      {s.text}
    </span>
  );
}

function fmtDate(iso: string | null | undefined) {
  if (!iso) return null;
  return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

async function requireAccessToken() {
  const { data: { session } } = await supabase.auth.getSession();
  const t = session?.access_token;
  if (!t) throw new Error("You're signed out. Please sign in again.");
  return t;
}

type Draft = { last_done_date: string; notes: string; provider: string };

export default function HealthScreenings({ rows: rowsProp, onRowsChange, isPostmeno = false }: {
  rows?: HealthScreeningRow[];
  onRowsChange?: (rows: HealthScreeningRow[]) => void;
  isPostmeno?: boolean;
}) {
  const list = useServerFn(listHealthScreenings);
  const save = useServerFn(saveHealthScreening);
  const del = useServerFn(deleteHealthScreening);

  const [rows, setRowsState] = useState<HealthScreeningRow[]>(rowsProp ?? []);
  const [loaded, setLoaded] = useState(rowsProp !== undefined);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<ScreeningKey | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);

  const setRows = (next: HealthScreeningRow[]) => {
    setRowsState(next);
    onRowsChange?.(next);
  };

  useEffect(() => {
    if (rowsProp !== undefined) {
      setRowsState(rowsProp);
      setLoaded(true);
    }
  }, [rowsProp]);

  useEffect(() => {
    if (rowsProp !== undefined) return;
    let active = true;
    (async () => {
      try {
        const accessToken = await requireAccessToken().catch(() => null);
        if (!accessToken || !active) { setLoaded(true); return; }
        const r = await list({ data: { accessToken } });
        if (!active) return;
        setRows(Array.isArray(r) ? r : []);
        setLoaded(true);
      } catch (e) {
        console.warn("listHealthScreenings skipped", e);
        if (active) setLoaded(true);
      }
    })();
    return () => { active = false; };
  }, []);

  const byKey: Record<string, HealthScreeningRow> = {};
  for (const r of rows) byKey[r.screening_type] = r;

  const visibleScreenings = SCREENINGS.filter((s) => isPostmeno || !s.postmenoOnly);

  const counts = { up_to_date: 0, due_soon: 0, overdue: 0, never_logged: 0 };
  for (const s of visibleScreenings) counts[statusFor(byKey[s.key])]++;

  const openRow = (key: ScreeningKey) => {
    setError(null);
    if (expanded === key) {
      setExpanded(null);
      setDraft(null);
      return;
    }
    const existing = byKey[key];
    setExpanded(key);
    setDraft({
      last_done_date: existing?.last_done_date ?? "",
      notes: existing?.notes ?? "",
      provider: existing?.provider ?? "",
    });
  };

  const handleSave = async (key: ScreeningKey) => {
    if (!draft) return;
    if (!draft.last_done_date) { setError("Please enter the date this screening was last done."); return; }
    setBusy(true); setError(null);
    try {
      const accessToken = await requireAccessToken();
      const saved = await save({ data: {
        accessToken,
        screening_type: key,
        last_done_date: draft.last_done_date,
        notes: draft.notes.trim() || null,
        provider: draft.provider.trim() || null,
      }});
      const next = [...rows.filter(r => r.screening_type !== key), saved]
        .sort((a, b) => a.screening_type.localeCompare(b.screening_type));
      setRows(next);
      setExpanded(null); setDraft(null);
    } catch (err: any) {
      setError(err?.message || "Couldn't save this screening.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (key: ScreeningKey) => {
    const row = byKey[key];
    if (!row) return;
    if (!confirm("Delete this screening entry?")) return;
    setBusy(true); setError(null);
    try {
      const accessToken = await requireAccessToken();
      await del({ data: { accessToken, id: row.id } });
      setRows(rows.filter(r => r.id !== row.id));
      setExpanded(null); setDraft(null);
    } catch (err: any) {
      setError(err?.message || "Couldn't delete this screening.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ background: WH, borderRadius: 20, padding: "22px 20px" }}>
      <div style={{ marginBottom: 4, fontSize: 16, fontWeight: 500, color: GD, letterSpacing: "-0.01em" }}>
        Health screenings
      </div>
      <div style={{ fontSize: 12, color: GR, lineHeight: 1.55, marginBottom: 14 }}>
        Track your routine checks. We'll remind you when you're due.
      </div>

      <div style={{ background: GL, borderRadius: 12, padding: "10px 13px", marginBottom: 14, fontSize: 11, color: GD, lineHeight: 1.5 }}>
        {counts.up_to_date} up to date · {counts.due_soon} due soon · {counts.overdue} overdue · {counts.never_logged} not logged
      </div>

      {error && (
        <div style={{ background: "#FFD0D9", borderRadius: 12, padding: "10px 13px", marginBottom: 12, fontSize: 12, color: RO, lineHeight: 1.5 }}>
          {error}
        </div>
      )}

      {!loaded && (
        <div style={{ fontSize: 12, color: GR, padding: "8px 4px" }}>Loading…</div>
      )}

      {loaded && visibleScreenings.map((s) => {
        const row = byKey[s.key];
        const status = statusFor(row);
        const isOpen = expanded === s.key;
        return (
          <div key={s.key} style={{ borderRadius: 14, border: `1px solid ${isOpen ? "#FFB0BD" : "#E8D5DA"}`, marginBottom: 10, background: WH, overflow: "hidden" }}>
            <button
              onClick={() => openRow(s.key)}
              style={{ width: "100%", background: "none", border: "none", padding: "12px 14px", cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 10 }}
            >
              <div style={{ fontSize: 20, flexShrink: 0 }}>{s.emoji}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: GD, lineHeight: 1.35 }}>
                  {s.name} <span style={{ color: GR, fontWeight: 400 }}>{s.interval}</span>
                </div>
                {row?.last_done_date && (
                  <div style={{ fontSize: 11, color: GR, marginTop: 2, lineHeight: 1.4 }}>
                    Last done: {fmtDate(row.last_done_date)}
                    {row.next_due_date && <> · Next due: {fmtDate(row.next_due_date)}</>}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                {statusPill(status)}
                <span style={{ color: GR, fontSize: 12, transform: isOpen ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>›</span>
              </div>
            </button>

            {isOpen && draft && (
              <div style={{ padding: "4px 14px 14px", borderTop: "1px solid #F3E6EA" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10, marginTop: 12, marginBottom: 12 }}>
                  <div>
                    <label style={{ fontSize: 11, color: GR, display: "block", marginBottom: 4 }}>Last done *</label>
                    <input
                      type="date"
                      value={draft.last_done_date}
                      max={new Date().toISOString().slice(0,10)}
                      onChange={(e) => setDraft({ ...draft, last_done_date: e.target.value })}
                      style={{ width: "100%", padding: "9px 10px", borderRadius: 10, border: "1px solid #E8D5DA", fontSize: 13, color: GD, background: WH }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: GR, display: "block", marginBottom: 4 }}>Provider (optional)</label>
                    <input
                      type="text"
                      value={draft.provider}
                      placeholder="e.g. Dr. Lee at Mass General"
                      onChange={(e) => setDraft({ ...draft, provider: e.target.value.slice(0, 255) })}
                      style={{ width: "100%", padding: "9px 10px", borderRadius: 10, border: "1px solid #E8D5DA", fontSize: 13, color: GD, background: WH }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: GR, display: "block", marginBottom: 4 }}>Notes (optional)</label>
                    <input
                      type="text"
                      value={draft.notes}
                      placeholder="Results, next steps, anything to remember"
                      onChange={(e) => setDraft({ ...draft, notes: e.target.value.slice(0, 2000) })}
                      style={{ width: "100%", padding: "9px 10px", borderRadius: 10, border: "1px solid #E8D5DA", fontSize: 13, color: GD, background: WH }}
                    />
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => { setExpanded(null); setDraft(null); setError(null); }}
                    disabled={busy}
                    style={{ flex: 1, padding: 11, borderRadius: 12, background: GL, color: GD, border: "none", fontSize: 13, fontWeight: 500, cursor: busy ? "wait" : "pointer" }}
                  >
                    Cancel
                  </button>
                  {row && (
                    <button
                      onClick={() => handleDelete(s.key)}
                      disabled={busy}
                      style={{ flex: 1, padding: 11, borderRadius: 12, background: "#FFD0D9", color: RO, border: "none", fontSize: 13, fontWeight: 500, cursor: busy ? "wait" : "pointer" }}
                    >
                      Delete
                    </button>
                  )}
                  <button
                    onClick={() => handleSave(s.key)}
                    disabled={busy}
                    style={{ flex: 2, padding: 11, borderRadius: 12, background: RO, color: WH, border: "none", fontSize: 13, fontWeight: 500, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1 }}
                  >
                    {busy ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
